import api from './api';

export type ReportSource = 'EXECUTIVE' | 'SALES_TEAM' | 'SALESPERSON' | 'PIPELINE' | 'REVENUE' | 'CLIENTS' | 'SUPPLIERS' | 'PRODUCTS' | 'INVENTORY' | 'PURCHASING' | 'QUOTES' | 'SAMPLES';
export type ReportFormat = 'PDF' | 'XLSX' | 'CSV';
export type ReportViewType = 'TABULAR' | 'SUMMARY' | 'MATRIX';

export interface ReportField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'status';
  filterable?: boolean;
  groupable?: boolean;
}

export interface ReportSourceCatalog {
  id: ReportSource;
  name: string;
  description: string;
  icon: string;
  color: string;
  dimensions: ReportField[];
  metrics: ReportField[];
  defaultColumns: string[];
}

export interface ReportConfig {
  schemaVersion?: number;
  source: ReportSource;
  viewType?: ReportViewType;
  title?: string;
  description?: string;
  dateRange?: { preset?: string; start?: string; end?: string };
  comparison?: 'NONE' | 'PREVIOUS_PERIOD' | 'PREVIOUS_YEAR';
  columns?: string[];
  metrics?: string[];
  dimensions?: string[];
  filters?: Array<{ field: string; operator: 'eq' | 'neq' | 'contains' | 'in' | 'gte' | 'lte'; value: string | number | Array<string | number> }>;
  filterLogic?: 'AND' | 'OR';
  entityFilters?: Record<string, Array<string | number>>;
  sorts?: Array<{ key: string; direction: 'asc' | 'desc' }>;
  topN?: number;
  rowGroups?: string[];
  columnGroup?: string;
  dateGranularity?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  charts?: Array<{ type: 'line' | 'bar' | 'stacked-bar' | 'donut' | 'funnel'; title: string; dimension: string; metrics: string[] }>;
  companyId?: number;
  layout?: { orientation?: 'portrait' | 'landscape' | 'auto'; showCover?: boolean; showFilters?: boolean; showDetails?: boolean };
}

export interface StandardReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  version: number;
  source: ReportSource;
  viewType: ReportViewType;
  isStandard: true;
  config: ReportConfig;
}

export interface ReportCatalog {
  schemaVersion: number;
  limits: Record<string, number>;
  viewTypes: ReportViewType[];
  chartTypes: string[];
  comparisonModes: string[];
  datePresets: string[];
  sources: ReportSourceCatalog[];
  templates: StandardReportTemplate[];
  realDataOnly: true;
}

export interface SavedReportDefinition {
  id: number;
  ownerId: number;
  name: string;
  description?: string;
  source: ReportSource;
  viewType: ReportViewType;
  config: ReportConfig;
  visibility: 'PRIVATE' | 'SHARED';
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { schedules: number; runs: number };
}

export interface ReportResult {
  meta: {
    title: string;
    source: ReportSource;
    viewType: ReportViewType;
    generatedAt: string;
    dateRange: { start: string; end: string; label: string };
    timezone: string;
    currency: string;
    organizationName: string;
    company?: { id: number; name: string; logo?: string } | null;
    dataCoverage: number;
    isRealData: true;
    layout?: ReportConfig['layout'];
    appliedFilters?: string[];
  };
  kpis: Array<{ key: string; label: string; value: number | string; format: 'number' | 'currency' | 'percent' | 'text'; currency?: string; definition: string; coverage?: number; delta?: number | null }>;
  charts: Array<{ key: string; title: string; type: string; data: Array<{ label: string; value: number; secondary?: number }> }>;
  columns: ReportField[];
  rows: Array<Record<string, string | number | boolean | null>>;
  totals: Record<string, number>;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  insights: Array<{ severity: 'info' | 'success' | 'warning' | 'critical'; title: string; message: string }>;
  warnings: string[];
  coverage: Array<{ key: string; label: string; covered: number; total: number; percent: number }>;
}

export interface ReportRun {
  id: number;
  definitionId?: number;
  scheduleId?: number;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  format: ReportFormat;
  title: string;
  fileName?: string;
  fileSize?: number;
  rowCount: number;
  error?: string;
  attempts: number;
  generatedAt?: string;
  emailedAt?: string;
  expiresAt?: string;
  createdAt: string;
}

export interface ReportSchedule {
  id: number;
  definitionId: number;
  companyId?: number;
  format: ReportFormat;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  timezone: string;
  deliveryTime: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  subject?: string;
  message?: string;
  isActive: boolean;
  nextRunAt?: string;
  lastRunAt?: string;
  definition: { id: number; name: string; source: ReportSource };
  _count?: { runs: number };
}

export interface DataQualityResult {
  reportingCurrency: string;
  score: number;
  totalIssues: number;
  emailConfigured: boolean;
  issues: Array<{ key: string; label: string; count: number; severity: 'critical' | 'warning'; action: string }>;
}

const reportsAPI = {
  getCatalog: async (): Promise<ReportCatalog> => (await api.get('/reports/catalog')).data,
  getOptions: async (type: string, search?: string): Promise<{ data: Array<{ id: number; label: string; meta?: string }> }> => (await api.get('/reports/options', { params: { type, search } })).data,
  preview: async (payload: { definitionId?: number; definition?: ReportConfig; filters?: Partial<ReportConfig>; page?: number; limit?: number }): Promise<ReportResult> => (await api.post('/reports/preview', payload)).data,
  getDefinitions: async (): Promise<SavedReportDefinition[]> => (await api.get('/reports/definitions')).data,
  createDefinition: async (payload: { name: string; description?: string; source: ReportSource; viewType: ReportViewType; config: ReportConfig; visibility?: 'PRIVATE' | 'SHARED'; isFavorite?: boolean }): Promise<SavedReportDefinition> => (await api.post('/reports/definitions', payload)).data,
  updateDefinition: async (id: number, payload: Partial<SavedReportDefinition>): Promise<SavedReportDefinition> => (await api.patch(`/reports/definitions/${id}`, payload)).data,
  cloneDefinition: async (id: number): Promise<SavedReportDefinition> => (await api.post(`/reports/definitions/${id}/clone`)).data,
  deleteDefinition: async (id: number): Promise<void> => { await api.delete(`/reports/definitions/${id}`); },
  createRun: async (payload: { definitionId?: number; definition?: ReportConfig; format: ReportFormat; filters?: Partial<ReportConfig> }): Promise<{ id: number; status: ReportRun['status'] }> => (await api.post('/reports/runs', payload)).data,
  getRun: async (id: number): Promise<ReportRun> => (await api.get(`/reports/runs/${id}`)).data,
  getRuns: async (params?: { page?: number; limit?: number; status?: string }): Promise<{ data: ReportRun[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => (await api.get('/reports/runs', { params })).data,
  retryRun: async (id: number): Promise<void> => { await api.post(`/reports/runs/${id}/retry`); },
  downloadRun: async (id: number, fileName: string): Promise<void> => {
    const response = await api.get(`/reports/runs/${id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  emailRun: async (id: number, payload: { to: string[]; cc?: string[]; bcc?: string[]; subject: string; message?: string }): Promise<void> => { await api.post(`/reports/runs/${id}/email`, payload); },
  getSchedules: async (): Promise<ReportSchedule[]> => (await api.get('/reports/schedules')).data,
  createSchedule: async (payload: Omit<ReportSchedule, 'id' | 'definition' | '_count' | 'nextRunAt' | 'lastRunAt'>): Promise<ReportSchedule> => (await api.post('/reports/schedules', payload)).data,
  updateSchedule: async (id: number, payload: Partial<ReportSchedule>): Promise<ReportSchedule> => (await api.patch(`/reports/schedules/${id}`, payload)).data,
  deleteSchedule: async (id: number): Promise<void> => { await api.delete(`/reports/schedules/${id}`); },
  getDataQuality: async (): Promise<DataQualityResult> => (await api.get('/reports/data-quality')).data,
  getExchangeRates: async (): Promise<Array<{ id: number; fromCurrency: string; toCurrency: string; rate: string; effectiveAt: string; source?: string }>> => (await api.get('/reports/exchange-rates')).data,
  createExchangeRate: async (payload: { fromCurrency: string; toCurrency: string; rate: number; effectiveAt: string; source?: string }): Promise<void> => { await api.post('/reports/exchange-rates', payload); },
};

export default reportsAPI;
